import React, { useState, useEffect } from "react";
import { fetchAuthors, createAuthor, deleteAuthor } from "../api";

export default function AdminAuthorManager({ user }) {
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [name, setName] = useState("");
    const [role, setRole] = useState("");
    const [bio, setBio] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadAuthors();
    }, []);

    const loadAuthors = async () => {
        setLoading(true);
        try {
            const data = await fetchAuthors();
            setAuthors(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setImageFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return alert("Author name is required");

        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append("name", name);
            formData.append("role", role);
            formData.append("bio", bio);
            if (imageFile) {
                formData.append("image", imageFile);
            }

            await createAuthor(user.token, formData);
            setName("");
            setRole("");
            setBio("");
            setImageFile(null);
            if (document.getElementById("author-image-upload")) {
                document.getElementById("author-image-upload").value = "";
            }
            await loadAuthors();
        } catch (err) {
            alert(err.message || "Failed to create author");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this author?")) return;
        try {
            await deleteAuthor(user.token, id);
            await loadAuthors();
        } catch (err) {
            alert(err.message || "Failed to delete author");
        }
    };

    if (loading && authors.length === 0) return <div>Loading authors...</div>;

    return (
        <div style={{ background: "var(--paper)", padding: "24px", borderRadius: "16px", border: "1px solid var(--border)" }}>
            <h2 style={{ color: "var(--forest)", marginTop: 0, marginBottom: "24px" }}>Manage Authors</h2>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "32px" }}>
                {/* Add New Author Form */}
                <div style={{ background: "#f8f9fa", padding: "20px", borderRadius: "12px", border: "1px solid var(--border)", alignSelf: "start" }}>
                    <h3 style={{ marginTop: 0, marginBottom: "16px", fontSize: "1.1rem" }}>Add New Author</h3>
                    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div>
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, fontSize: "0.9rem" }}>Author Name *</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Dr. A.P.J. Abdul Kalam"
                                required
                                style={{
                                    width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid var(--border)",
                                    fontFamily: "inherit"
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, fontSize: "0.9rem" }}>Role (Optional)</label>
                            <input
                                type="text"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                placeholder="e.g. Author, Bestseller"
                                style={{
                                    width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid var(--border)",
                                    fontFamily: "inherit"
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, fontSize: "0.9rem" }}>Bio (Optional)</label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                placeholder="Short author biography..."
                                rows="3"
                                style={{
                                    width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid var(--border)",
                                    fontFamily: "inherit", resize: "vertical"
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, fontSize: "0.9rem" }}>Author Photo (Optional)</label>
                            <input
                                id="author-image-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{
                                    width: "100%", padding: "8px", borderRadius: "8px", border: "1px solid var(--border)",
                                    background: "white", fontSize: "0.9rem"
                                }}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn-forest"
                            disabled={submitting}
                            style={{ padding: "12px", marginTop: "8px" }}
                        >
                            {submitting ? "Adding..." : "+ Add Author"}
                        </button>
                    </form>
                </div>

                {/* Author List */}
                <div>
                    <h3 style={{ marginTop: 0, marginBottom: "16px", fontSize: "1.1rem" }}>Current Authors</h3>
                    {error && <div style={{ color: "red", marginBottom: "16px" }}>{error}</div>}
                    
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "16px" }}>
                        {authors.length === 0 ? (
                            <p style={{ color: "var(--text-light)" }}>No authors added yet.</p>
                        ) : authors.map(author => (
                            <div key={author.id} style={{ 
                                background: "white", borderRadius: "12px", padding: "16px", border: "1px solid var(--border)",
                                display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "12px",
                                position: "relative"
                            }}>
                                <button
                                    onClick={() => handleDelete(author.id)}
                                    style={{
                                        position: "absolute", top: "8px", right: "8px", background: "#dc3545", color: "white",
                                        border: "none", borderRadius: "50%", width: "24px", height: "24px", cursor: "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", lineHeight: 1
                                    }}
                                    title="Delete Author"
                                >
                                    ✕
                                </button>
                                
                                <div style={{ 
                                    width: "80px", height: "80px", borderRadius: "50%", background: "#f0f0f0",
                                    backgroundImage: author.image_url ? `url(${author.image_url})` : "none",
                                    backgroundSize: "cover", backgroundPosition: "center", flexShrink: 0,
                                    border: "2px solid rgba(0,0,0,0.05)", display: "flex", alignItems: "center", justifyContent: "center"
                                }}>
                                    {!author.image_url && <span style={{ fontSize: "24px", color: "#ccc" }}>👤</span>}
                                </div>
                                <div style={{ width: "100%" }}>
                                    <div style={{ fontWeight: 600, fontSize: "0.95rem", wordBreak: "break-word" }}>{author.name}</div>
                                    {author.role && <div style={{ fontSize: "0.8rem", color: "var(--forest)", marginTop: "2px" }}>{author.role}</div>}
                                    {author.bio && <div style={{ fontSize: "0.75rem", color: "var(--text-light)", marginTop: "4px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{author.bio}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
